import {Component, OnInit} from '@angular/core';
import {debounceTime, distinctUntilChanged, mergeMap, startWith, take, takeUntil, tap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {Actions, ofActionSuccessful, Select, Store} from '@ngxs/store';
import {InvoiceState} from '../../store/invoice/invoice.state';
import {Observable, Subject, forkJoin} from 'rxjs';
import {Invoice} from '@shared/models/invoice/invoice.model';
import {
  ChangeCurrencyAction,
  FindAllCurrencyAction,
  FindInvoiceByIdAction,
  GetAllTaxByCountryAction,
  GetInvoiceLoadsLimit,
  GetOrgDetailsAction,
  GetRateCardsByIds,
  RemoveAllLoadsAction,
  RemoveLoadAction, UpdateExpensesAction,
  UpdateInvoiceAction,
  UploadPdfAndGetId
} from '../../store/invoice/invoice.actions';
import {AuthState} from '@core/store/auth/auth.state';
import {User} from '@core/models/user.model';
import {EntityMap} from '@shared/models/types';
import {OrgDetails} from '@shared/models/org-details.model';
import {InvoiceLoad} from '../../models/invoice-load.model';
import {DialogHandlerService} from '@core/services/dialog-handler.service';
import {AddCostComponent} from '../../components/add-cost/add-cost.component';
import {AddLoadDialogComponent} from '../../components/add-load-dialog/add-load-dialog.component';
import {Cost} from '../../models/cost.model';
import {Step} from '@shared/models/step.model';
import {ProgressLineStepType} from '../../models/enums/progress-line-step.enum';
import {ApprovalStateType} from '../../models/enums/approval-state.enum';
import {OverdueStateType} from '../../models/enums/overdue-state.enum';
import {TabType} from '../../models/enums/invoice-state.enum';
import {PaymentType} from '../../models/enums/payment-type.enum';
import {AddAdditionAndSubtractionComponent} from '../../components/add-addition-and-subtraction/add-addition-and-subtraction.component';
import {AdditionAndSubtraction} from '../../models/addition-and-subtraction.model';
import {Tax} from '../../models/tax.model';
import {ChangeCurrencyComponent} from '../../components/change-currency/change-currency.component';
import {InvoiceCurrency} from '../../models/invoice-currency.model';
import {Currency} from '@shared/models/currency.model';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {RateCard} from '@shared/models/rate-card.model';
import {PaymentTerm} from '@shared/models/payment-term.model';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Location} from '@angular/common';
import * as uuid from 'uuid';
import {InvoiceExtraService} from '../../services/invoice-extra.service';
import {InvoiceType} from '../../models/enums/invoice-type.enum';
import { FeeType } from '@shared/models/enums/commission-fee.type';
import { RateSheetType } from '@shared/models/enums/rate-sheet-type.enum';
import { RateCardManagerState } from 'app/modules/rate-card-manager/state/rate-card-manager.state';
import { FetchCountries } from 'app/modules/rate-card-manager/state/rate-card-manager.actions';
import { MatDialog } from '@angular/material/dialog';
import {Expense} from '../../models/expense.model';
import {SuccessPopupComponent} from '../../components/success-popup/success-popup.component';
import {RejectReasonDialogComponent} from '../../components/reject-reason-dialog/reject-reason-dialog.component';
import {InvoiceChatService} from '../../services/invoice-chat.service';

export type InlineKind =
  | 'load-fee'
  | 'load-additional-cost'
  | 'load-item'
  | 'load-discount'
  | 'load-commission'
  | 'invoice-additional'
  | 'invoice-item'
  | 'invoice-discount'
  | 'invoice-tax';

export type InlineMode = 'add' | 'edit';

export interface InlineEditorState {
  kind: InlineKind;
  mode: InlineMode;
  loadId?: string;
  editingId?: string;
  source?: any;
}

@Component({
    selector: 'hmt-invoice-progress-line',
    templateUrl: './invoice-progress-line.component.html',
    styleUrls: ['./invoice-progress-line.component.scss'],
    standalone: false
})
export class InvoiceProgressLineComponent implements OnInit {
  @Select(InvoiceState.getSelectedInvoice) invoice$: Observable<Invoice>;
  @Select(InvoiceState.getOrgDetails) orgDetails$: Observable<EntityMap<string, OrgDetails>>;
  @Select(InvoiceState.getAllTaxes) taxes$: Observable<Tax[]>;
  @Select(InvoiceState.getCurrencies) currencies$: Observable<Currency[]>;
  @Select(AuthState.getUser) user$: Observable<User>;
  @Select(InvoiceState.getRateCardsMap) rateCardsMap$: Observable<EntityMap<string, RateCard>>;
  @Select(InvoiceState.getInvoiceLoadsLimit) loadsLimit$: Observable<number>;
  @Select(RateCardManagerState.getCurrencies) currenciesAll$: Observable<string[]>;
  private unsubscribe: Subject<void> = new Subject();
  user: User;
  invoice: Invoice;
  orgIds: string[];
  progressLineStep = ProgressLineStepType;
  approvalStep = ApprovalStateType;
  invoiceStep = TabType;
  overdueStep = OverdueStateType;
  paymentType = PaymentType;
  totalsAfterTax: number[] = [];
  taxAmounts: number[] = [];
  rateCardContainerIdsMap: EntityMap<string, string[]> = {};
  rateCardIds: string[];
  oldCurrencyRate = 0;
  tenantLoadsLength = 5;
  currencyMap: EntityMap<string, string> = {};
  steps: Step[] = this.invoiceExtraService.invoicePLSteps;
  groupByJobTitle: EntityMap<string, InvoiceLoad[]> = {};
  groups: string[] = [];
  suspendedVat = false;
  taxes: AdditionAndSubtraction[];
  toggled = true;
  expandedLoads: Set<string> = new Set<string>();
  rateForm: FormGroup;

  inlineEditor: InlineEditorState | null = null;
  inlineForm: FormGroup;
  taxList: Tax[] = [];

  chatOpen = false;
  supplierOrg: OrgDetails;
  customerOrg: OrgDetails;
  openedFromSentInvoices = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store, private action$: Actions,
    public dialogHandlerService: DialogHandlerService,
    private location: Location,
    public invoiceExtraService: InvoiceExtraService,
    public matDialog: MatDialog,
    private invoiceChatService: InvoiceChatService
  ) {
  }

  isChatEligible(invoice: Invoice): boolean {
    return this.invoiceChatService.canChat(invoice, this.user?.orgId);
  }

  openChat(): void {
    this.chatOpen = true;
  }

  closeChat(): void {
    this.chatOpen = false;
  }

  ngOnInit(): void {
    this.initializeData();
  }

  initializeData() {
    this.rateForm = new FormGroup({
      rate: new FormControl(''),
    });
    this.inlineForm = new FormGroup({
      description: new FormControl(''),
      percentage: new FormControl<number | null>(null),
      amount: new FormControl<number | null>(null),
      taxName: new FormControl(''),
    });
    this.taxes$
      .pipe(takeUntil(this.unsubscribe), tap(list => (this.taxList = list || [])))
      .subscribe();
    this.user$.pipe(
      takeUntil(this.unsubscribe),
      tap((user: User) => {
        this.user = user;
      })
    ).subscribe();
    this.orgDetails$.pipe(
      takeUntil(this.unsubscribe),
      tap((map: EntityMap<string, OrgDetails>) => {
        if (!map || !this.invoice) {
          return;
        }
        this.supplierOrg = map[this.invoice.serviceProvider];
        this.customerOrg = map[this.invoice.createdTo];
      })
    ).subscribe();
    this.route.queryParams
      .pipe(
        takeUntil(this.unsubscribe),
        tap((params) => {
          this.openedFromSentInvoices = (params.from || '').includes('/sent-invoices');
          this.store$.dispatch(new FindInvoiceByIdAction(params.id));
        }),
      ).subscribe();
    this.invoice$.pipe(
      takeUntil(this.unsubscribe),
      tap((invoice: Invoice) => {
        this.orgIds = [invoice?.createdTo, invoice?.serviceProvider];
        const observables = [
          this.store$.dispatch(new GetOrgDetailsAction(this.orgIds)),
          this.store$.dispatch(new GetInvoiceLoadsLimit(this.user.orgId))
        ];
        forkJoin(observables).subscribe(() => {
        this.invoice = invoice;
        if (this.invoice?.loads?.length > 0) {
          this.groupByJobTitle = this.invoice.loads.reduce((group, load) => {
            const {jobTitle} = load;
            group[jobTitle] = group[jobTitle] ?? [];
            group[jobTitle].push(load);
            return group;
          }, {});
          this.groups = Object.keys(this.groupByJobTitle);

          this.rateCardContainerIdsMap = this.invoice.loads.reduce((group, load) => {
            const {rateCard} = load;
            group[rateCard] = group[rateCard] ?? [];
            group[rateCard].push(load.loadNumber ? load.loadNumber : load.loadId);
            return group;
          }, {});
          this.rateCardIds = Object.keys(this.rateCardContainerIdsMap);
          this.store$.dispatch(new GetRateCardsByIds(this.rateCardIds));
        }
        else{
          this.groupByJobTitle = {};
        }
        if (this.invoice != null) {
          if (this.invoice.defaultOrgCurrencyCode === this.invoice.currencyCode && !(this.invoice.exchangeRate > 0)) {
            this.invoice = {...this.invoice, exchangeRate: 1};
          }
          this.rateForm.controls.rate.setValue(this.invoice.exchangeRate);
          this.suspendedVat = invoice.suspendedVat;
          this.taxes = invoice.taxes;
          this.calculateTax();

        }
        });
      })
    ).subscribe();

    this.rateForm.get('rate')
      .valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        startWith(''),
        debounceTime(1000),
        distinctUntilChanged(),
        tap((rate: number | string) => {
          if (!this.invoice || rate === '' || rate === null || isNaN(Number(rate))) {
            return;
          }
          this.invoice = {...this.invoice, exchangeRate: Number(rate)};
          this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
        })
      )
      .subscribe();

    this.store$.dispatch(new GetAllTaxByCountryAction('LK'));
    this.store$.dispatch(new FindAllCurrencyAction());

    this.action$.pipe(
      ofActionSuccessful(ChangeCurrencyAction),
      takeUntil(this.unsubscribe),
      tap(() => {
        const pt =  {...this.invoice.invoicePaymentTerm};
        pt.advance = (pt.advance * this.oldCurrencyRate) / this.invoice.currency.currencyRate;
        const invoicePaymentTermStr = this.invoiceExtraService.generatePaymentTermString(pt, this.invoice.currency, this.getTotal());
        this.invoice = {...this.invoice, invoicePaymentTermStr, invoicePaymentTerm: pt};
        this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
      })
    ).subscribe();

    this.action$.pipe(
      ofActionSuccessful(GetInvoiceLoadsLimit),
      take(1),
      mergeMap(() => this.loadsLimit$),
      tap((limit: number) => {
        this.tenantLoadsLength = limit;
        // this.toggled = (this.invoice?.loads?.length <= this.tenantLoadsLength);
      })
    ).subscribe();

    this.currencies$.pipe(
      takeUntil(this.unsubscribe),
      tap(currencies => {
        this.currencyMap = currencies.reduce((map, ic) => {
          map[ic.code] = ic.shortCode;
          return map;
        }, {});
      })
    ).subscribe();
    this.store$.dispatch(new FetchCountries());
    this.currenciesAll$.pipe(
      tap((res) => console.log(res))
    ).subscribe();
  }

  removeLoadSubTotal(loadId: string){
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = {...loads[index], addedOtherCost: [], addedDiscount: null};
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  removeLoadDiscount( loadId: string){
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = {...loads[index], addedDiscount: null};
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  removeLoadOtherCost(id: string, loadId: string) {
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    const currentOtherCosts = this.invoice.loads[index]?.addedOtherCost || [];
    loads[index] = { ...loads[index], addedOtherCost: currentOtherCosts.filter(cost => cost.id !== id) };
    this.invoice = { ...this.invoice, loads };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  removeLoadAdditionalCost(id: string, loadId: string) {
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    const currentAdditionalCosts = this.invoice.loads[index]?.addedAdditionalCost || [];
    loads[index] = { ...loads[index], addedAdditionalCost: currentAdditionalCosts.filter(cost => cost.id !== id) };
    this.invoice = { ...this.invoice, loads };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  removeCost(id: string, loadId: string) {
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = {...loads[index], costs: [...this.invoice.loads[index].costs.filter(cost => cost.id !== id)]};
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
    const expenses = loads[index].costs.map(cost => ({
        id: cost.id,
        costDescription: cost.description,
        amount: cost.value,
        paymentType: PaymentType.AMOUNT,
        feeType: cost.feeType
      } as Expense));
    this.store$.dispatch(new UpdateExpensesAction(loads[index]._id, expenses));
  }


  addLoadAdditionalCost(loadId: string, lineItem: Cost) {
    if (lineItem == null) {
      this.startInlineAdd('load-additional-cost', loadId);
    } else {
      this.startInlineEdit('load-additional-cost', lineItem, loadId);
    }
  }

  private applyLoadAdditionalCost(loadId: string, cost: Cost, original: Cost | null): void {
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    const currentAdditionalCosts = this.invoice.loads[index]?.addedAdditionalCost || [];
    if (original == null) {
      loads[index] = { ...loads[index], addedAdditionalCost: [...currentAdditionalCosts, cost] };
    } else {
      const addedAdditionalCost = currentAdditionalCosts.map(value => (cost.id === value.id ? cost : value));
      loads[index] = { ...loads[index], addedAdditionalCost };
    }
    this.invoice = { ...this.invoice, loads };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }


  addCost(loadId: string, lineItem: Cost) {
    if (lineItem == null) {
      this.startInlineAdd('load-fee', loadId);
    } else {
      this.startInlineEdit('load-fee', lineItem, loadId);
    }
  }

  private applyLoadFee(loadId: string, cost: Cost, original: Cost | null): void {
    cost.feeType = original?.feeType;
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    if (original == null) {
      loads[index] = {...loads[index], costs: [...this.invoice.loads[index].costs, cost]};
    } else {
      const costs = [...this.invoice.loads[index].costs].map(value => (cost.id === value.id ? cost : value));
      loads[index] = {...loads[index], costs};
    }
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
    const expenses = loads[index].costs.map(c => ({
      id: c.id,
      costDescription: c.description,
      amount: c.value,
      paymentType: PaymentType.AMOUNT,
      feeType: c.feeType
    } as Expense));
    this.store$.dispatch(new UpdateExpensesAction(loads[index]._id, expenses));
  }

  removeLoad(id: string, invoiceId: string) {
    this.store$.dispatch(new RemoveLoadAction(id, InvoiceType.RECEIVABLE, invoiceId));
  }

  openAddLoadDialog() {
    if (!this.invoice || !this.checkPermission(this.invoice)) {
      return;
    }
    this.dialogHandlerService.openDialog(AddLoadDialogComponent, {
      invoice: this.invoice
    }, {
      width: '980px',
      maxWidth: '95vw',
      height: '85vh',
      panelClass: 'custom-dialog-container',
      autoFocus: false,
      position: {top: '5%'},
    });
  }

  addAdditionalCost() {
    this.startInlineAdd('invoice-additional');
  }

  private applyInvoiceAdditional(cost: Cost): void {
    this.invoice = {...this.invoice, additionalCosts: [...this.invoice.additionalCosts, cost]};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  removeAdditionalCost(id: string) {
    this.invoice = {...this.invoice, additionalCosts: [...this.invoice.additionalCosts.filter(cost => cost.id !== id)]};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  calculateTotalOfLoadsCost() {
    return this.invoice?.loads.reduce((total, load) => total + load.loadCost, 0);
  }

  onSaveInvoice() {
    this.invoice = {...this.invoice, total: +(this.totalsAfterTax[this.totalsAfterTax.length - 1])?.toFixed(2)};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Saved Successfully'));
  }

  private showSuccessPopup(message: string): void {
    this.matDialog.open(SuccessPopupComponent, {
      data: { message },
      panelClass: 'success-popup-panel',
      width: '550px',
      autoFocus: false,
      restoreFocus: false,
      backdropClass: 'success-popup-backdrop',
    });
  }

  openLoadAddItem(loadId: string, item: AdditionAndSubtraction) {
    if (item == null) {
      this.startInlineAdd('load-item', loadId);
    } else {
      this.startInlineEdit('load-item', item, loadId);
    }
  }

  private applyLoadItem(loadId: string, cost: AdditionAndSubtraction, original: AdditionAndSubtraction | null): void {
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    const currentOtherCosts = this.invoice.loads[index]?.addedOtherCost || [];
    if (original == null) {
      loads[index] = { ...loads[index], addedOtherCost: [...currentOtherCosts, cost] };
    } else {
      const addedOtherCost = currentOtherCosts.map(value => (cost.id === value.id ? cost : value));
      loads[index] = { ...loads[index], addedOtherCost };
    }
    this.invoice = { ...this.invoice, loads };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  openLoadAddDiscount(loadId: string, item: AdditionAndSubtraction) {
    if (item == null) {
      this.startInlineAdd('load-discount', loadId);
    } else {
      this.startInlineEdit('load-discount', item, loadId);
    }
  }

  private applyLoadDiscount(loadId: string, cost: AdditionAndSubtraction): void {
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = { ...loads[index], addedDiscount: cost };
    this.invoice = { ...this.invoice, loads };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }



  openAddItem() {
    this.startInlineAdd('invoice-item');
  }

  private applyInvoiceItem(cost: AdditionAndSubtraction): void {
    this.invoice = {...this.invoice, otherCosts: [...this.invoice.otherCosts, cost]};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  openAddDiscount() {
    this.startInlineAdd('invoice-discount');
  }

  editDiscount(discount: AdditionAndSubtraction) {
    this.startInlineEdit('invoice-discount', discount);
  }

  private applyInvoiceDiscount(discount: AdditionAndSubtraction): void {
    this.invoice = {...this.invoice, discount};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  openAddTax() {
    this.startInlineAdd('invoice-tax');
  }

  openEditTax(tax: AdditionAndSubtraction) {
    this.startInlineEdit('invoice-tax', tax);
  }

  private applyTaxAdd(event: AdditionAndSubtraction): void {
    this.invoice = {...this.invoice, taxes: [...this.invoice.taxes, event]};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  private applyTaxEdit(originalId: string, event: AdditionAndSubtraction): void {
    const index = this.invoice.taxes.findIndex(t => t.id === originalId);
    const taxes = [...this.invoice.taxes];
    taxes[index] = event;
    this.invoice = {...this.invoice, taxes};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  calculateAddedDiscountTotal(load: InvoiceLoad){
    const totalLoadCost = this.calculateLoadSubTotal(load);
    const calculatedDiscount = (totalLoadCost / 100) * (load?.addedDiscount == null ? 0 : load?.addedDiscount?.value);
    const totalLoadDiscountedCost = load.addedDiscount == null ? 0
      : (load?.addedDiscount?.paymentType === PaymentType.AMOUNT ? load?.addedDiscount?.value : parseFloat(calculatedDiscount.toFixed(2)));
    return totalLoadDiscountedCost;
  }

  removeAddedDiscountOfLoad(loadId: string){
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = {...loads[index], addedDiscount: null};
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  calculateAddedItemTotal(load: InvoiceLoad){
    const totalLoadCost = this.calculateLoadSubTotal(load);
    const totalLoadOtherCost = load?.addedOtherCost?.reduce(
      (total, cost) =>
        total +
        (cost?.paymentType === PaymentType.PERCENTAGE
          ? (totalLoadCost / 100) * (cost?.value || 0)
          : cost?.value || 0),
      0
    ) || 0;
    return parseFloat(totalLoadOtherCost.toFixed(2));
  }

  removeAddedItemOfLoad(loadId: string){
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = {...loads[index], addedOtherCost: []};
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }


  removeLineItem(lineItem: Cost) {
    this.invoice = {...this.invoice, otherCosts: [...this.invoice.otherCosts.filter(item => item.id !== lineItem.id)]};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  removeLoadsCost(id: string) {
    this.invoice = {...this.invoice, loadsCosts: [...this.invoice.loadsCosts.filter(cost => cost.id !== id)]};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  addLoadsCost(loadsCost: Cost) {
    const data = {cost: loadsCost};
    const dialogRef = this.dialogHandlerService.openDialog(AddCostComponent, data, {
      height: '350px',
      width: '30%',
      panelClass: 'custom-dialog-container',
      position: {
        top: '10%',
        left: '35%'
      },
    });

    dialogRef.componentInstance.addCost.pipe(
      takeUntil(this.unsubscribe),
      tap((cost: Cost) => {
        if (loadsCost == null){
          cost.value = this.convertToDefaultCurrency(cost.value);
          this.invoice = {...this.invoice, loadsCosts: [...this.invoice.loadsCosts, cost]};
        }
        else{
          const index  = this.invoice.loadsCosts.findIndex( item => item.id === loadsCost.id);
          cost.value = this.convertToDefaultCurrency(cost.value);
          const lc = [...this.invoice.loadsCosts];
          lc[index] = cost;
          this.invoice = {...this.invoice, loadsCosts: lc};
        }
        this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
      })
    ).subscribe();
  }

  removeDiscount() {
    this.invoice = {...this.invoice, discount: null};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  removeTax(id: string) {
    this.invoice = {...this.invoice, taxes: [...this.invoice.taxes.filter(tax => tax.id !== id)]};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }


  calculateLoadPercentage(load: InvoiceLoad, value: number) {
    let commissionTotal = 0;
    if (load?.commissions) {
      load.commissions.forEach(commission => {
        if (commission?.feeType) {
          commission.feeType.forEach(feeType => {
            commissionTotal += this.calculateCommisions(commission, load, feeType);
          });
        }
      });
    }

    const loadCost = load?.loadCost || 0;
    const loadExpense = (load?.costs?.reduce((total, load) => total + (load?.value || 0), 0) || 0) +
      (load?.addedAdditionalCost?.reduce((total, load) => total + (load?.value || 0), 0) || 0);
    return (loadCost + loadExpense + commissionTotal) * value / 100;
  }

  calculateLoadSubTotal(load: InvoiceLoad) {

    let commissionTotal = 0;
    if (load?.commissions) {
      load.commissions.forEach(commission => {
        if (commission?.feeType) {
          commission.feeType.forEach(feeType => {
            commissionTotal += this.calculateCommisions(commission, load, feeType);
          });
        }
      });
    }

    const totalLoadCost =
      (load?.loadCost || 0) +
      (load?.costs?.reduce((totalCost, cost) => totalCost + (cost?.value || 0), 0) || 0) +
      (load?.addedAdditionalCost?.reduce((totalCost, cost) => totalCost + (cost?.value || 0), 0) || 0) +
      commissionTotal;

    return totalLoadCost;
  }

  calculateTotalOfAllLoads() {
    return this.invoice?.loads.reduce((total, load) => total + this.calculateLoadTotal(load), 0);
  }

  calculateLoadTotal(load: InvoiceLoad) {
    const totalLoadCost = this.calculateLoadSubTotal(load);
    const totalLoadOtherCost = load?.addedOtherCost?.reduce(
      (total, cost) =>
        total +
        (cost?.paymentType === PaymentType.PERCENTAGE
          ? (totalLoadCost / 100) * (cost?.value || 0)
          : cost?.value || 0),
      0
    ) || 0;
    const calculatedDiscount = (totalLoadCost / 100) * (load?.addedDiscount == null ? 0 : load?.addedDiscount?.value);
    const totalLoadDiscountedCost = load.addedDiscount == null ? 0
      : (load?.addedDiscount?.paymentType === PaymentType.AMOUNT ? load?.addedDiscount?.value : parseFloat(calculatedDiscount.toFixed(2)));
    return totalLoadCost + parseFloat(totalLoadOtherCost.toFixed(2)) - totalLoadDiscountedCost;
  }

  calculateServiceFee() {
    const additionalCostTotal = this.invoice?.additionalCosts.reduce((total, cost) => total + cost.value, 0);
    const loadCostTotal = this.invoice?.loads.reduce((total, load) => total + this.calculateLoadTotal(load), 0);
    return additionalCostTotal + loadCostTotal;
  }

  calculateLineItemValue(value: number) {
    const serviceFee = this.calculateServiceFee();
    return serviceFee / 100 * value;
  }

  calculateDiscount() {
    const serviceFee = this.calculateServiceFee();
    return serviceFee / 100 * this.invoice.discount?.value;
  }

  calculateSubTotal() {
    const serviceFee = this.calculateServiceFee();
    const lineItemsTotal = this.invoice?.otherCosts.reduce((total, cost) => total + (cost.paymentType === PaymentType.PERCENTAGE ? (serviceFee / 100 * cost.value) : cost.value ), 0);
    const serviceFeePlusLineItems = serviceFee + parseFloat(lineItemsTotal.toFixed(2))  ;
    const calculatedDiscount = (serviceFee / 100) * (this.invoice?.discount == null ? 0 : this.invoice?.discount?.value);
    const discount = this.invoice?.discount == null ? 0
      : (this.invoice?.discount.paymentType === PaymentType.AMOUNT ? this.invoice?.discount?.value : parseFloat(calculatedDiscount.toFixed(2)));
    return serviceFeePlusLineItems - discount;
  }

  calculateTax() {
    const totalsAfterTax: number[] = [];
    const taxAmounts: number[] = [];
    const subTotal = this.calculateSubTotal();

    if (this.taxes.length <= 0) {
      totalsAfterTax[0] = subTotal;
    } else {
      if (this.suspendedVat) {
        this.taxes = this.invoice?.taxes.filter(tax => tax.description !== 'Vat');
      } else {
        this.taxes = this.invoice?.taxes;
      }
      if (this.taxes.length <= 0) {
        totalsAfterTax[0] = subTotal;
      }
      this.taxes.forEach((tax, index) => {
        if (index === 0) {
          totalsAfterTax[index]
            = tax.paymentType === PaymentType.AMOUNT ? (tax.value + subTotal) : parseFloat(((subTotal / 100) * tax.value).toFixed(2)) + subTotal;
          taxAmounts[index] = tax.paymentType === PaymentType.AMOUNT ? tax.value : parseFloat(((subTotal / 100) * tax.value).toFixed(2));
        } else {
          totalsAfterTax[index] = tax.paymentType === PaymentType.AMOUNT ? (tax.value + totalsAfterTax[index - 1])
            : parseFloat(((totalsAfterTax[index - 1] / 100) * tax.value).toFixed(2)) + totalsAfterTax[index - 1];
          taxAmounts[index] = tax.paymentType === PaymentType.AMOUNT ? tax.value : parseFloat(((totalsAfterTax[index - 1] / 100) * tax.value).toFixed(2));
        }
      });
    }
    this.totalsAfterTax = totalsAfterTax;
    this.taxAmounts = taxAmounts;
  }

  getTotal() {
    return this.taxes?.length > 0 ? this.totalsAfterTax[this.totalsAfterTax.length - 1] : this.calculateSubTotal();
  }

  onSaveAndResubmit() {
    this.invoice = {
      ...this.invoice,
      total: +(this.totalsAfterTax[this.totalsAfterTax.length - 1])?.toFixed(2),
      approvalState: ApprovalStateType.CUSTOMER_PENDING_APPROVAL,
      stateUpdatedAt: new Date()
    };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Resubmitted Successfully'));
  }

  onSubmitForApproval() {
    this.invoice = {
      ...this.invoice, progressStep: ProgressLineStepType.SENT_FOR_INTERNAL_APPROVAL,
      approvalState: ApprovalStateType.SEND_FOR_INTERNAL_APPROVAL,
      stateUpdatedAt: new Date()
    };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Submitted for Approval Successfully'));
  }

  onInternalReject() {
    this.promptRejectReason().subscribe((reason) => {
      if (!reason) { return; }
      const now = new Date();
      this.invoice = {
        ...this.invoice, progressStep: ProgressLineStepType.SENT_FOR_INTERNAL_APPROVAL,
        approvalState: ApprovalStateType.INTERNALLY_REJECTED,
        stateUpdatedAt: now,
        rejectedBy: this.user?.userId,
        rejectedDueTo: reason,
        rejectedDate: now,
      };
      this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
        .subscribe(() => this.showSuccessPopup('Invoice Rejected Successfully'));
    });
  }

  private promptRejectReason(): Observable<string | undefined> {
    return this.matDialog.open(RejectReasonDialogComponent, {
      width: '660px',
      maxWidth: '90vw',
      panelClass: 'reject-reason-panel',
      autoFocus: false,
      restoreFocus: false,
    }).afterClosed();
  }

  onInternalApprove() {
    this.invoice = {
      ...this.invoice, progressStep: ProgressLineStepType.INTERNAL_APPROVED,
      approvalState: ApprovalStateType.INTERNALLY_APPROVED,
      stateUpdatedAt: new Date()
    };
    this.invoice = this.generateInvoiceCalculationDetails(this.invoice);
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Approved Successfully'));
  }

  downloadPdf() {
    this.store$.dispatch(new UploadPdfAndGetId(this.user.orgId, this.invoice._id));
  }

  sentToCustomer() {
    this.invoice = {
      ...this.invoice, progressStep: ProgressLineStepType.SENT, tab: TabType.UNPAID,
      approvalState: ApprovalStateType.CUSTOMER_PENDING_APPROVAL,
      stateUpdatedAt: new Date()
    };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Sent to Customer Successfully'));
  }

  markAsOverdue() {
    this.invoice = {...this.invoice, overdueState: OverdueStateType.OVERDUE, tab: TabType.OVERDUE,
      stateUpdatedAt: new Date()};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Marked as Overdue Successfully'));
  }

  markAsPaid() {
    this.invoice = {
      ...this.invoice, progressStep: ProgressLineStepType.PAID, tab: TabType.PAID,
      approvalState: ApprovalStateType.PAID,
      stateUpdatedAt: new Date()
    };
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Marked as Paid Successfully'));
  }

  onCustomerReject() {
    this.promptRejectReason().subscribe((reason) => {
      if (!reason) { return; }
      const now = new Date();
      this.invoice = {
        ...this.invoice, progressStep: ProgressLineStepType.SENT,
        approvalState: ApprovalStateType.CUSTOMER_REJECTED,
        stateUpdatedAt: now,
        rejectedBy: this.user?.userId,
        rejectedDueTo: reason,
        rejectedDate: now,
      };
      this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
        .subscribe(() => this.showSuccessPopup('Customer Rejection Recorded Successfully'));
    });
  }

  onCustomerApprove() {
    this.invoice = {...this.invoice, progressStep: ProgressLineStepType.SENT,
      approvalState: ApprovalStateType.CUSTOMER_APPROVED, stateUpdatedAt: new Date()};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Customer Approval Recorded Successfully'));
  }

  markAsDoubtful() {
    this.invoice = {...this.invoice, overdueState: OverdueStateType.DOUBTFUL,
      tab: TabType.OVERDUE, stateUpdatedAt: new Date()};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Marked as Doubtful Successfully'));
  }

  markAsSubtended() {
    this.invoice = {...this.invoice, overdueState: OverdueStateType.SUBSTANDARD,
      tab: TabType.OVERDUE, stateUpdatedAt: new Date()};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Marked as Substandard Successfully'));
  }

  maskAsLost() {
    this.invoice = {...this.invoice, overdueState: OverdueStateType.LOSS,
      tab: TabType.OVERDUE, stateUpdatedAt: new Date()};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice))
      .subscribe(() => this.showSuccessPopup('Invoice Marked as Loss Successfully'));
  }

  backToInvoicePage() {
    this.location.back();
  }

  checkPermission(invoice: Invoice): boolean {
    return invoice.tab === this.invoiceStep.DRAFT
      || (invoice.tab === this.invoiceStep.UNPAID && invoice.approvalState === this.approvalStep.CUSTOMER_REJECTED)
      || invoice.approvalState === this.approvalStep.DISPUTED;
  }

  removeAllLoads(id: string) {
    this.store$.dispatch(new RemoveAllLoadsAction(id, InvoiceType.RECEIVABLE));
  }

  getGroupByContainerType(invoiceLoadsForJob: InvoiceLoad[]): EntityMap<string, InvoiceLoad[]> {
    return invoiceLoadsForJob.reduce((group, load) => {
      const {containerType} = load;
      group[containerType] = group[containerType] ?? [];
      group[containerType].push(load);
      return group;
    }, {});
  }

  getContainerTypes(invoiceLoadsForJob: InvoiceLoad[]): string[] {
    const groupByContainerTypeMap = this.getGroupByContainerType(invoiceLoadsForJob);
    return Object.keys(groupByContainerTypeMap);
  }

  applyCurrency(value: number) {
    if (!this.invoice?.currency) {
      return '';
    }
    value = value / this.invoice.currency.currencyRate;
    const formattedNumber = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return  this.invoice.currency.currentCurrency + ' ' + formattedNumber;
  }

  applyCurrencyWithoutCurrencyCode(value: number): number {
    if (!this.invoice?.currency) {
      return 0;
    }
    return +(value / this.invoice.currency.currencyRate)?.toFixed(2);
  }

  convertToDefaultCurrency(value: number): number {
    if (!this.invoice?.currency) {
      return value;
    }
    if (this.invoice.currency.currentCurrency !== this.invoice.currency.defaultCurrency) {
      return value * this.invoice.currency.currencyRate;
    } else {
      return value;
    }
  }

  changeCurrency() {
    const data = {
      invoice: this.invoice,
      currencies: this.currencies$,
      currenciesAll: this.currenciesAll$
    };
    const dialogRef = this.matDialog.open(ChangeCurrencyComponent, {
      autoFocus: false,
      height: '350px',
      width: '30%',
      panelClass: 'custom-dialog-container',
      position: {
        top: '10%',
        left: '35%'
      },data
    });

    dialogRef.componentInstance.changeCurrency.pipe(
      takeUntil(this.unsubscribe),
      tap((currency: InvoiceCurrency) => {
        this.oldCurrencyRate = this.invoice.currency.currencyRate;
        this.invoice = {...this.invoice, currency};
        this.store$.dispatch(new ChangeCurrencyAction(this.invoice));
      })
    ).subscribe();
  }

  checkSavedOrNot() {
    return this.invoice?.total !== +(this.totalsAfterTax[this.totalsAfterTax.length - 1])?.toFixed(2);
  }

  onChangeSVat($event: MatCheckboxChange) {
    if ($event.checked) {
      this.suspendedVat = true;
      this.invoice = {...this.invoice, suspendedVat: true};
    } else {
      this.suspendedVat = false;
      this.invoice = {...this.invoice, suspendedVat: false};
    }
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
    this.calculateTax();
  }

  calculateSVat() {
    const vat = this.invoice.taxes.filter(tax => tax.description === 'Vat');
    return (this.calculateSubTotal() / 100) * vat[0]?.value;
  }


  addPaymentTerm($event: { paymentTerm: PaymentTerm; isValidPaymentTerm: boolean }) {
    if ($event.isValidPaymentTerm) {
      const invoicePaymentTermStr = this.invoiceExtraService
        .generatePaymentTermString($event.paymentTerm, this.invoice.currency, this.getTotal());
      this.invoice = {...this.invoice, invoicePaymentTerm: $event.paymentTerm, invoicePaymentTermStr};
      this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
    }
  }

  toggleLoads() {
    this.toggled = !this.toggled;
    if (this.toggled) {
      this.invoice?.loads?.forEach(load => this.expandedLoads.add(load.loadId));
    } else {
      this.expandedLoads.clear();
    }
    this.invoice = {...this.invoice, loadsExpandState: this.toggled};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  isLoadExpanded(loadId: string): boolean {
    return this.expandedLoads.has(loadId);
  }

  toggleLoadExpand(loadId: string): void {
    if (this.expandedLoads.has(loadId)) {
      this.expandedLoads.delete(loadId);
    } else {
      this.expandedLoads.add(loadId);
    }
  }

  calculateCommisions(commission: Cost, load: InvoiceLoad, feeType: string) {
    // WEIGHT_BASED contracts are invoiced without commissions.
    if (load?.rateSheetType === RateSheetType.WEIGHT_BASED) {
      return 0;
    }
    if(feeType === FeeType.TRANSPORTATION_FEES  ) {
      const total = commission.value * load.loadCost / 100;
      return parseFloat(total.toFixed(2));
    }
    if(feeType === FeeType.EXTRA_KILOMETER_CHARGE) {
      let total = 0;
      load.costs.forEach(expense => {
        if(expense.feeType?.includes(FeeType.EXTRA_KILOMETER_CHARGE)) {
          total += expense.value;
        }
      });
      const totalCommission = commission.value * total / 100;
      return parseFloat(totalCommission.toFixed(2));
    }
    if(feeType === FeeType.DEMURRAGE) {
      let total = 0;
      load.costs.forEach(expense => {
        if(expense.feeType?.includes(FeeType.DEMURRAGE)) {
          total += expense.value;
        }
      });
      const totalCommission = commission.value * total / 100;
      return parseFloat(totalCommission.toFixed(2));
    }
    if(feeType === FeeType.FIXED_COSTS) {
      let total = 0;
      load.costs.forEach(expense => {
        if(expense.feeType?.includes(FeeType.FIXED_COSTS)) {
          total += expense.value;
        }
      });
      const totalCommission = commission.value * total / 100;
      return parseFloat(totalCommission.toFixed(2));
    }
    if(feeType === FeeType.ADDITIONAL_EXPENSES) {
      let total = 0;
      load.costs.forEach(expense => {
        if(expense.feeType?.includes(FeeType.ADDITIONAL_EXPENSES)) {
          total += expense.value;
        }
      });
      const totalCommission = commission.value * total / 100;
      return parseFloat(totalCommission.toFixed(2));
    }

    return 0;
  }

  removeCommission(commission: Cost , feeType: string, loadId: string) {
    const commissionRemovedFeeType = commission.feeType.filter(type => type !== feeType);
    if(commissionRemovedFeeType.length === 0) {
      this.removeAllCommission(commission, loadId);
    }else{
      const commissionRemoved = {...commission, feeType:commissionRemovedFeeType};
      const loads = [...this.invoice.loads];
      const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = {...loads[index], commissions:[...this.invoice.loads[index].commissions.filter(cost => cost.id !== commission.id), commissionRemoved]};
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
    }

  }

  removeAllCommission(commission: Cost, loadId: string) {
    const loads = [...this.invoice.loads];
      const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
      loads[index] = {...loads[index], commissions:[...this.invoice.loads[index].commissions.filter(cost => cost.id !== commission.id)]};
      this.invoice = {...this.invoice, loads};
      this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  editCommission(commission: Cost, loadId: string) {
    this.startInlineEdit('load-commission', commission, loadId);
  }

  private applyCommissionEdit(loadId: string, original: Cost, edited: Cost): void {
    const commissionData = {
      value: edited.value,
      paymentType: this.paymentType.PERCENTAGE,
      id: edited.id,
      description: edited.description,
      feeType: original.feeType
    };
    const loads = [...this.invoice.loads];
    const index = this.invoice.loads.findIndex(l => l.loadId === loadId);
    loads[index] = {
      ...loads[index],
      commissions: [...this.invoice.loads[index].commissions.filter(c => c.id !== original.id), commissionData]
    };
    this.invoice = {...this.invoice, loads};
    this.store$.dispatch(new UpdateInvoiceAction(this.invoice));
  }

  calculateTotalCommissions(commission: Cost, load: InvoiceLoad) {
    let commissionTotal = 0;
    commission.feeType.forEach(feeType => {
      const commissionValue = this.calculateCommisions(commission, load, feeType);
      commissionTotal += commissionValue;
    });
    return commissionTotal;
  }

  generateInvoiceCalculationDetails(invoice: Invoice) {
    if (invoice.otherCosts != null && invoice.otherCosts.length > 0){
      invoice.otherCosts = invoice.otherCosts?.map(cost => ({
        ...cost, // Spread operator creates a new object
        calculatedValue: cost.paymentType === PaymentType.AMOUNT ? cost.value : this.calculateLineItemValue(cost.value),
      }));
    }

    if (invoice.discount != null){
      invoice.discount = {
        ...invoice.discount,
        calculatedValue: invoice.discount.paymentType === PaymentType.AMOUNT
          ? invoice.discount.value
          : this.calculateDiscount(),
      };
    }

    invoice.subTotal = this.calculateSubTotal();

    if (invoice.taxes != null && invoice.taxes.length > 0){
      const i = 0;
      invoice.taxes = invoice.taxes?.map((tax, index) => ({
        ...tax,
        calculatedValue: this.taxAmounts[index],
      }));
    }

    invoice.total = this.getTotal();
    invoice.serviceFee = this.calculateServiceFee();
    return invoice;
  };

  isInlineActive(kind: InlineKind, mode: InlineMode, loadId?: string, editingId?: string): boolean {
    const e = this.inlineEditor;
    if (!e) {
      return false;
    }
    if (e.kind !== kind || e.mode !== mode) {
      return false;
    }
    if (loadId !== undefined && e.loadId !== loadId) {
      return false;
    }
    if (editingId !== undefined && e.editingId !== editingId) {
      return false;
    }
    return true;
  }

  showsField(name: 'description' | 'percentage' | 'amount' | 'taxName'): boolean {
    if (!this.inlineEditor) {
      return false;
    }
    const k = this.inlineEditor.kind;
    switch (name) {
      case 'description':
        return k !== 'invoice-tax';
      case 'taxName':
        return k === 'invoice-tax';
      case 'percentage':
        return k === 'load-discount' || k === 'load-commission'
          || k === 'invoice-item' || k === 'invoice-discount' || k === 'invoice-tax';
      case 'amount':
        return k === 'load-fee' || k === 'load-additional-cost'
          || k === 'load-item' || k === 'load-discount'
          || k === 'invoice-additional' || k === 'invoice-item' || k === 'invoice-discount';
    }
    return false;
  }

  startInlineAdd(kind: InlineKind, loadId?: string): void {
    this.inlineEditor = { kind, mode: 'add', loadId };
    this.resetInlineForm();
  }

  startInlineEdit(kind: InlineKind, source: any, loadId?: string): void {
    this.inlineEditor = {
      kind,
      mode: 'edit',
      loadId,
      editingId: source?.id,
      source
    };
    this.prefillInlineFormFromSource(kind, source);
  }

  cancelInline(): void {
    this.inlineEditor = null;
    this.resetInlineForm();
  }

  private resetInlineForm(): void {
    this.inlineForm.reset({ description: '', percentage: null, amount: null, taxName: '' });
  }

  private prefillInlineFormFromSource(kind: InlineKind, source: any): void {
    const isPercent = source?.paymentType === PaymentType.PERCENTAGE;
    if (kind === 'invoice-tax') {
      this.inlineForm.reset({
        description: '',
        taxName: source?.description ?? '',
        percentage: source?.value ?? null,
        amount: null,
      });
      return;
    }
    if (kind === 'load-commission') {
      this.inlineForm.reset({
        description: source?.description ?? '',
        percentage: source?.value ?? null,
        amount: null,
        taxName: '',
      });
      return;
    }
    if (kind === 'load-fee' || kind === 'load-additional-cost' || kind === 'invoice-additional') {
      const amt = source?.value != null ? source.value / (this.invoice?.currency?.currencyRate || 1) : null;
      this.inlineForm.reset({
        description: source?.description ?? '',
        percentage: null,
        amount: amt,
        taxName: '',
      });
      return;
    }
    // load-item / load-discount / invoice-item / invoice-discount
    const amtVal = source?.value != null
      ? (isPercent ? null : source.value / (this.invoice?.currency?.currencyRate || 1))
      : null;
    this.inlineForm.reset({
      description: source?.description ?? '',
      percentage: isPercent ? (source?.value ?? null) : null,
      amount: amtVal,
      taxName: '',
    });
  }

  onTaxNameChange(taxName: string): void {
    const match = this.taxList.find(t => t.name === taxName);
    if (match) {
      this.inlineForm.patchValue({ percentage: match.percentage });
    }
  }

  saveInline(): void {
    if (!this.inlineEditor) {
      return;
    }
    const { kind, mode, loadId, source } = this.inlineEditor;
    const raw = this.inlineForm.value;
    const description: string = (raw.description ?? '').toString().trim();
    const taxName: string = (raw.taxName ?? '').toString().trim();
    const pct = raw.percentage != null && raw.percentage !== '' ? Number(raw.percentage) : 0;
    const amt = raw.amount != null && raw.amount !== '' ? Number(raw.amount) : 0;

    if (kind === 'invoice-tax') {
      if (!taxName || !(pct > 0)) {
        return;
      }
      const obj: AdditionAndSubtraction = {
        id: mode === 'edit' ? source.id : uuid.v4(),
        value: pct,
        description: taxName,
        paymentType: PaymentType.PERCENTAGE,
        calculatedValue: 0,
      };
      if (mode === 'add') {
        this.applyTaxAdd(obj);
      } else {
        this.applyTaxEdit(source.id, obj);
      }
      this.cancelInline();
      return;
    }

    if (kind === 'load-commission') {
      if (!description || !(pct > 0)) {
        return;
      }
      const edited: Cost = {
        id: source.id,
        value: pct,
        description,
        feeType: source?.feeType,
      };
      this.applyCommissionEdit(loadId!, source, edited);
      this.cancelInline();
      return;
    }

    if (kind === 'load-fee' || kind === 'load-additional-cost' || kind === 'invoice-additional') {
      if (!description || !(amt > 0)) {
        return;
      }
      const cost: Cost = {
        id: mode === 'edit' ? source.id : uuid.v4(),
        value: this.convertToDefaultCurrency(amt),
        description,
      };
      if (kind === 'load-fee') {
        this.applyLoadFee(loadId!, cost, mode === 'edit' ? source : null);
      } else if (kind === 'load-additional-cost') {
        this.applyLoadAdditionalCost(loadId!, cost, mode === 'edit' ? source : null);
      } else {
        this.applyInvoiceAdditional(cost);
      }
      this.cancelInline();
      return;
    }

    // load-item (amount only) / load-discount / invoice-item / invoice-discount
    const isAmountOnly = kind === 'load-item';
    const hasPercent = !isAmountOnly && pct > 0;
    const hasAmount = amt > 0;
    if (!description || (!hasPercent && !hasAmount)) {
      return;
    }
    const pType = isAmountOnly ? PaymentType.AMOUNT : (hasPercent ? PaymentType.PERCENTAGE : PaymentType.AMOUNT);
    const value = hasPercent ? pct : this.convertToDefaultCurrency(amt);
    const obj: AdditionAndSubtraction = {
      id: mode === 'edit' ? source.id : uuid.v4(),
      value,
      description,
      paymentType: pType,
      calculatedValue: 0,
    };

    if (kind === 'load-item') {
      this.applyLoadItem(loadId!, obj, mode === 'edit' ? source : null);
    } else if (kind === 'load-discount') {
      this.applyLoadDiscount(loadId!, obj);
    } else if (kind === 'invoice-item') {
      this.applyInvoiceItem(obj);
    } else if (kind === 'invoice-discount') {
      this.applyInvoiceDiscount(obj);
    }
    this.cancelInline();
  }
}
